import { type IClient, matchClient, matchNumber, normalizeName } from './clients.js';
import { soleApplicant, taxPeriod } from './detail.js';
import { type IFilingFields, type INamingRules, type IRendered, isValidDate, render } from './template.js';
import { ACCOUNTING_CA, deadlinesFor, deadlineWords, docTypeOf, fieldsOf, type IPack } from '../packs/index.js';

/** What the AI read from one document (before any matching or rules). */
export interface IExtraction {
  /** YYYY-MM-DD as the AI read it. */
  date?: string;
  dateConfidence?: number;
  /** The client as written in the document. */
  client?: string;
  clientConfidence?: number;
  /** Who issued or sent the document, as written (decides Invoice vs Bill). */
  issuer?: string;
  /** Other organizations on the confirmed list that the document also mentions. */
  otherClients?: string[];
  docType?: string;
  docTypeConfidence?: number;
  detail?: string;
  /** Tax or fiscal year when it differs from the date's year. */
  year?: number;
  /** One short sentence for people. */
  reason?: string;
  /** The AI service found what looks like instructions hidden in this document: a person checks it. */
  flagged?: boolean;
  /** AI-designed structures: the folder chosen from the library's folders, and a title for the name. */
  folder?: string;
  folderConfidence?: number;
  title?: string;
  /** The pack's extra facts for this type (e.g. expiryDate), as read: dates YYYY-MM-DD. */
  extra?: { [fieldId: string]: string };
  /**
   * The document's own words each fact was read from, checked by groundInText and then dropped:
   * it is document text, so it is never kept in the queue, a list or a log.
   */
  evidence?: { date?: string; client?: string; docType?: string };
}

/** One extra fact, ready to show or write: its label and value. */
export interface IExtraValue {
  id: string;
  label: string;
  value: string;
}

export interface IDecision {
  fields: IFilingFields;
  rendered: IRendered;
  /** 0–1 per field, after matching against the client list. */
  confidence: { date: number; client: number; docType: number };
  action: 'file' | 'review';
  /** Why it needs a person, in plain language (empty when filed). */
  reasons: string[];
  /** The pack's extra facts read for this type (empty for most). */
  extra: IExtraValue[];
}

export const DEFAULT_THRESHOLD: number = 0.85;

/** Shown when the AI service's safety check found instructions hidden in a document. */
export const FLAGGED_REASON: string = 'This document contains text that looks like instructions aimed at the AI, so I left it for a person to check.';

/** Senders accountants know by their short form. */
const SHORT_FORMS: [RegExp, string][] = [
  [/^canada revenue agency$|^agence du revenu du canada$/i, 'CRA'],
  [/^(workplace safety and insurance board|wsib)( ontario)?$/i, 'WSIB'],
  [/^revenu qu[ée]bec$/i, 'RevenuQuebec'],
  [/^service canada$/i, 'ServiceCanada']
];

function shortForm(detail: string | undefined): string | undefined {
  const d: string = (detail ?? '').trim();
  return d ? (SHORT_FORMS.filter(([re]) => re.test(d)).map(([, s]) => s)[0] ?? d) : undefined;
}

function clamp(n: unknown): number {
  const v: number = Number(n);
  return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0;
}

/** The choice a value names, ignoring case and spaces: "in canada" is "In Canada". */
export function choiceOf(choices: string[], value: string | undefined): string | undefined {
  const key = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  return value ? choices.filter(c => key(c) === key(value))[0] : undefined;
}

function percent(n: number): string {
  return `${Math.round(n * 100)}%`;
}

/**
 * Turns an extraction into fields, a name, a destination and a decision. A document is filed
 * automatically only when the date, the client and the document type are each at or above the
 * threshold, the client is on the confirmed list, and the type isn't "Other". Everything else
 * goes to review with the reason spelled out.
 */
export function decide(x: IExtraction, extension: string, clients: IClient[], threshold: number = DEFAULT_THRESHOLD, rules?: INamingRules, pack: IPack = ACCOUNTING_CA): IDecision {
  const reasons: string[] = [];
  const entity: string = pack.entity.one;
  // The firm's own records (law: trust bank statements, ledgers…) need no client or matter.
  const organisation: boolean = docTypeOf(pack, x.docType)?.scope === 'organisation';

  const dateOk: boolean = isValidDate(x.date);
  const date: number = dateOk ? clamp(x.dateConfidence) : 0;
  if (!dateOk) {
    reasons.push('No clear date was found.');
  }

  const match = organisation ? undefined : matchClient(x.client, clients);
  // An exact matter number is certain on its own (the AI's own doubt is about wording, not the number).
  // So is a client's exact name when that client has only one matter (nothing to choose between).
  const byNumber: boolean = !!match && match.score === 1 && (!!matchNumber(x.client, clients)
    || (!!match.client.number && clients.filter(c => !!c.number && normalizeName(c.name) === normalizeName(match.client.name)).length === 1));
  let client: number = organisation || byNumber ? 1 : match ? Math.min(clamp(x.clientConfidence), match.score) : 0;
  if (organisation) {
    // Nothing to find.
  } else if (!x.client) {
    reasons.push(`No ${entity} was found.`);
  } else if (!match) {
    reasons.push(`"${x.client}" isn't on your ${entity} list.`);
  } else if (match.score < threshold) {
    reasons.push(`"${x.client}" isn't an exact match. Is it ${match.client.number ? `${match.client.number} (${match.client.name})` : match.client.name}?`);
  }
  const others: string[] = (organisation ? [] : x.otherClients ?? [])
    .map(o => matchClient(o, clients))
    .filter((m): m is NonNullable<typeof m> => !!m && m.score >= 0.8 && (!match || m.client.short !== match.client.short))
    .map(m => m.client.name);
  if (match && others.length > 0) {
    client = Math.min(client, 0.6);
    reasons.push(`It also mentions ${others.join(' and ')}. Which ${entity} is it for?`);
  }

  // "INVOICE" at the top doesn't say whose it is: issued by the client = Invoice (sales),
  // issued by anyone else to the client = Bill (a purchase), named after the vendor.
  let docTypeId: string | undefined = x.docType;
  let detail: string | undefined = x.detail?.trim() || undefined;
  if (match && x.issuer && (docTypeId === 'Invoice' || docTypeId === 'Bill')) {
    const issuer = matchClient(x.issuer, clients);
    const issuedByClient: boolean = !!issuer && issuer.score >= 0.8 && issuer.client.short === match.client.short;
    if (docTypeId === 'Invoice' && !issuedByClient) {
      docTypeId = 'Bill';
      detail = x.issuer;
    } else if (docTypeId === 'Bill' && issuedByClient) {
      docTypeId = 'Invoice';
    }
  }
  const type = docTypeOf(pack, docTypeId);
  // Some types settle the detail in code, so it doesn't vary with the AI's wording: a bill or a
  // letter is named after who sent it (never its invoice number or topic), a GST/HST return by its
  // period (Q1–Q4 or Annual).
  if (type?.detailRule === 'issuer' && x.issuer?.trim()) {
    detail = x.issuer.trim();
  } else if (type?.detailRule === 'taxPeriod') {
    detail = taxPeriod(detail);
  }
  detail = shortForm(detail);
  let docType: number = type ? clamp(x.docTypeConfidence) : 0;
  if (!type) {
    reasons.push('The document type is unclear.');
  } else if (type.id === 'Other') {
    docType = Math.min(docType, 0.5);
    reasons.push("It doesn't match a known document type.");
  }

  // Extra facts some types carry, read now: the applicant can be a folder level.
  const packFields = fieldsOf(pack, type?.id);
  const readExtra = (id: string): string | undefined => {
    const f = packFields.filter(p => p.id === id)[0];
    const raw: string | undefined = x.extra?.[id]?.trim() || undefined;
    if (!f) {
      return undefined;
    }
    // A fact with set values keeps only one of them ("refused" is Refused; anything else is dropped).
    return f.kind === 'date' ? (isValidDate(raw) ? raw : undefined) : f.choices ? choiceOf(f.choices, raw) : raw;
  };

  // Immigration: when the AI didn't name the applicant but the matter is certain and has only one
  // applicant, the document is about that person.
  const sure: boolean = !!match && client >= threshold && others.length === 0;
  const applicant: string | undefined = packFields.some(f => f.id === 'applicant')
    ? readExtra('applicant') ?? (sure ? soleApplicant(match!.client) : undefined)
    : undefined;
  const valueOf = (id: string): string | undefined => (id === 'applicant' ? applicant : readExtra(id));

  const fields: IFilingFields = {
    date: dateOk ? x.date : undefined,
    clientShort: match?.client.short,
    clientName: match?.client.name,
    clientFolder: match?.client.number ? `${match.client.number} - ${match.client.name}` : undefined,
    area: match?.client.area,
    docType: type?.id,
    detail,
    year: Number.isInteger(x.year) && x.year! >= 1990 && x.year! <= 2100 ? x.year : undefined,
    applicant
  };
  const rendered: IRendered = render(fields, extension, rules, pack);

  const low: string[] = [];
  if (dateOk && date < threshold) { low.push(`date (${percent(date)})`); }
  if (match && client < threshold && others.length === 0) { low.push(`${entity} (${percent(client)})`); }
  if (type && type.id !== 'Other' && docType < threshold) { low.push(`document type (${percent(docType)})`); }
  if (low.length > 0) {
    reasons.push(`Not sure enough about the ${low.join(', ')}.`);
  }

  // The pack's extra facts for this type. A date that starts a deadline is never filed on the
  // AI's word alone: a person confirms it, and one that wasn't found is asked for.
  const extra: IExtraValue[] = [];
  let mustConfirm: boolean = false;
  let mustCheck: boolean = false;
  for (const f of packFields) {
    const value: string | undefined = valueOf(f.id);
    if (value) {
      extra.push({ id: f.id, label: f.label, value });
    }
    if (value && f.review) {
      mustCheck = true;
      reasons.push(f.review.replace('{value}', value));
    }
    if (f.confirm) {
      mustConfirm = true;
      reasons.push(value ? `Please confirm the ${f.label.toLowerCase()} (${value}): it starts a deadline.` : `No ${f.label.toLowerCase()} was found. Please add it: it starts a deadline.`);
    }
  }
  // The deadline those facts start, worked out for the person confirming them (never calendared).
  for (const d of deadlinesFor(pack, type?.id, Object.fromEntries(extra.map(e => [e.id, e.value])))) {
    reasons.push(deadlineWords(d));
  }

  const action: 'file' | 'review' = rendered.name && date >= threshold && client >= threshold && docType >= threshold && !mustConfirm && !mustCheck ? 'file' : 'review';
  if (x.flagged) {
    return { fields, rendered, confidence: { date, client, docType }, action: 'review', reasons: [FLAGGED_REASON], extra };
  }
  return { fields, rendered, confidence: { date, client, docType }, action, reasons: action === 'file' ? [] : reasons, extra };
}
